import { APIConstants } from '@shared/constants';
import { ChatSenderType } from '@features/bid-chat/models/bid-chat.model';

const CHAT_API = APIConstants.API_URL_BASE + 'chat/';

/** The REST routes the bid chat calls (`/v0.0.1/api/chat`); the rest is Firestore. */
export const BID_CHAT_API = {
  /** Mints the Firebase custom token for the caller (chat contract §4.1). */
  FIREBASE_TOKEN: CHAT_API + 'firebase-token',
  /** Customer only, idempotent: opens (or returns) the negotiation conversation of one PENDING bid. */
  INITIATE_BID: CHAT_API + 'conversations/initiate-bid',
  /** CLOSED conversations only, offset-paginated (chat contract §4.3) — the Chat logs History. */
  HISTORY: CHAT_API + 'conversations/history',
  /**
   * Accept the fleet's active offer, by Firestore ids (conversation doc, offer message doc). The backend
   * finalizes the bid and updates Firestore; the web writes nothing for it.
   */
  ACCEPT_OFFER: (conversationId: string, messageId: string) => `${CHAT_API}conversations/${conversationId}/offers/${messageId}/accept`,
};

/**
 * The bid chat calls that handle their own failure, so the global error toast skips them: the token,
 * History and accept, by exact route (the host can be switched by `BaseAPI`), so a future `chat/`
 * endpoint still gets the global handling.
 */
const SILENT_BID_CHAT_ROUTES = /\/chat\/(firebase-token|conversations\/history|conversations\/[^/]+\/offers\/[^/]+\/accept)$/;

export const isSilentBidChatCall = (url: string): boolean => SILENT_BID_CHAT_ROUTES.test(url);

/**
 * A new-message push (FCM, `type: CHAT`): its `data` keys, and the query params of the link the
 * system notification opens when the tab is in the background (`?openChat=<conversation id>&chatShipmentId=<id>`).
 */
export const BID_CHAT_PUSH = {
  // The push sends the Firestore doc id under `conversationId`, not `firestoreConversationId`.
  conversationIdKey: 'conversationId',
  shipmentIdKey: 'shipmentId',
  linkConversationParam: 'openChat',
  linkShipmentParam: 'chatShipmentId',
};

/** `FleetBidStatus` values the chat cares about. */
export const CHAT_BID_STATUS = {
  Pending: 'PENDING',
  /** The price is settled: the chat stays open for text, offers stop. */
  Accepted: 'ACCEPTED',
} as const;

/** `body` of an OFFER message: a readable line for older apps and the push. */
export const offerBody = (price: number): string => `Offer: ${price} SAR`;

/** The price in an `offerBody()` preview ("Offer: 1200 SAR"), stored in English; `null` = a text message. */
export const offerPreviewPrice = (preview: string | null | undefined): number | null => {
  const offer = /^Offer: ([\d.,]+) SAR$/.exec(preview || '');
  return offer ? Number(offer[1].replace(/,/g, '')) : null;
};

/**
 * The customer's side of a Firestore negotiation doc (#47189). Active chats are queried on it:
 * `status == OPEN`, `bidStatus in [PENDING, ACCEPTED]` and `customerId == user.parentId`.
 */
export const CUSTOMER_CHAT_SIDE = {
  senderType: ChatSenderType.Customer,
  /** Inbox key: the customer company (`user.parentId`), so every user of the customer shares it. */
  inboxField: 'customerId',
  /**
   * `bidStatus` values that keep a chat in Active chats and writable: still negotiating, or accepted
   * (talks go on). Rejected or cancelled → History, read-only.
   */
  activeBidStatuses: [CHAT_BID_STATUS.Pending, CHAT_BID_STATUS.Accepted],
  /** My unread counter (read + reset). */
  unreadField: 'unreadCount.customer',
  /** The other side's counter, bumped on every send: the fleet's, or the individual driver's. */
  recipientUnreadFields: { fleet: 'unreadCount.fleet', driver: 'unreadCount.driver' },
} as const;

/** History filters: negotiation conversations only (`hasBid`). */
export const BID_CHAT_HISTORY_FILTERS = {
  hasBid: true,
};

export const BID_CHAT_LIMITS = {
  /** Firestore rules: body 1–4000 chars after trim. */
  BODY_MAX_LENGTH: 4000,
  SENDER_NAME_MAX_LENGTH: 100,
  PREVIEW_LENGTH: 120,
  /** Mandatory cap on the live Active chats query (contract §6.2). */
  INBOX_LIMIT: 50,
  /** History page (contract §4.3: default 50, max 100 — above 100 the server answers 400). */
  HISTORY_LIMIT: 50,
};
